<template>
  <div class="auction-monitor">
    <el-row :gutter="12">
      <el-col :span="18">
        <div class="toolbar">
          <el-button type="primary" @click="reload">刷新</el-button>
          <span class="update-time">{{ lastUpdate || '--:--:--' }}</span>
        </div>
        <div class="filters">
          <el-checkbox v-model="filterLeader">龙头</el-checkbox>
          <el-checkbox v-model="filterRank">龙虎</el-checkbox>
          <el-checkbox v-model="filterPurple">紫柱</el-checkbox>
          <el-checkbox v-model="filterSmile">笑脸</el-checkbox>
          <el-checkbox v-model="filterOpen">开盘</el-checkbox>
        </div>
        <div class="filters">
          <el-button
            v-for="opt in strategyOptions"
            :key="opt.value"
            size="small"
            :type="strategy === opt.value ? 'primary' : 'default'"
            @click="setStrategy(opt.value)"
          >
            {{ opt.label }}
          </el-button>
        </div>
        <div class="filters">
          <el-button size="small" :type="selectedSectorName === '' ? 'primary' : 'default'" @click="setSector('')">全部</el-button>
          <el-button
            v-for="it in displaySectors"
            :key="it.sector_name"
            size="small"
            :type="selectedSectorName === it.sector_name ? 'primary' : 'default'"
            @click="setSector(it.sector_name)"
          >
            {{ it.sector_name }}（{{ it.count }}）
          </el-button>
          <el-button v-if="sectors.length > 8" size="small" @click="sectorExpanded = !sectorExpanded">
            {{ sectorExpanded ? '收起' : '更多' }}
          </el-button>
        </div>
        <div class="table-scroll">
        <el-table :data="tableRows" stripe size="small" table-layout="fixed" style="min-width:700px" @selection-change="onSelectionChange">
          <el-table-column type="selection" width="30" />
          <el-table-column label="股票名称" min-width="120">
            <template #default="{row}">
              <div class="name-cell">
                <div class="row1">
                  <div class="left">
                    <div class="nm">{{ displayName(row.name) }}</div>
                    <div class="cd">{{ displayCode(row.ts_code) }}</div>
                  </div>
                  <div class="right">
                    <span class="momentum" v-if="displayMomentumSymbol(row)">{{ displayMomentumSymbol(row) }}</span>
                  </div>
                </div>
                <div class="flags">
                  <span v-if="sigBarsVisible(row)" class="sig-bars">
                    <span class="bar buy" :style="barStyleBuy(row.signal_buy)" :title="String(Number(row.signal_buy || 0))"></span>
                    <span class="bar sell" :style="barStyleSell(row.signal_sell)" :title="String(Number(row.signal_sell || 0))"></span>
                  </span>
                  <span class="leader" v-if="Number(row.is_leader) === 1">🐲</span>
                  <span class="rank" v-if="Number(row.is_rank) === 1">🐉</span>
                  <span class="strength" v-if="row.signal_strength">{{ String(row.signal_strength || '').replace(/\s+/g, '') }}</span>
                  <span class="score" v-if="scoreEmoji(row.score_total)">{{ scoreEmoji(row.score_total) }}</span>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="操作" min-width="100" align="center" fixed="right">
            <template #default="{row}">
              <el-button size="small" type="primary" plain @click="openIntraday(row)">分时</el-button>
              <el-button size="small" type="primary" plain @click="openKline(row)">K线</el-button>
            </template>
          </el-table-column>
          <el-table-column label="最新价" min-width="70" align="center">
            <template #default="{row}">
              <div class="price-cell">
                <span :class="['curr', pctClass(row.pct_chg), changedClass(row)]" :data-code="row.code6" :data-pct="row.pct_chg">
                  {{ formatPrice(row.close) }}
                  <span class="curr-sup">{{ formatPctIntWithSign(row.pct_chg) }}</span>
                </span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="昨日价" min-width="70" align="center">
            <template #default="{row}">
              <div class="price-cell">
                <span class="curr" :class="pctClass(row.pre_pct_chg)">
                  {{ formatPrice(row.pre_close) }}
                  <span class="curr-sup">{{ formatPctIntWithSign(row.pre_pct_chg) }}</span>
                </span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="今日量比" min-width="70" align="center">
            <template #default="{row}">
              <span class="value-top" :class="trendCompareClass(row.today_vol_ratio, row.pre_vol_ratio)">{{ formatNumber(row.today_vol_ratio) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="昨日量比" min-width="70" align="center">
            <template #default="{row}">
              <span class="value-top">{{ formatNumber(row.pre_vol_ratio) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="涨速" min-width="90" align="center">
            <template #default="{row}">
              <span class="value-top float-badge" :class="priceSpeedBadgeClass(row.price_speed_ratio)">
                {{ formatSpeed30(row.price_speed_ratio) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="净买" min-width="70" align="center">
            <template #default="{row}">
              <span class="value-top" :class="trendClass(Number(row.bigorder_net_shares_today || 0) / 1000000)">
                {{ formatNumber(Number(row.bigorder_net_shares_today || 0) / 1000000) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="买入量" min-width="70" align="center">
            <template #default="{row}">
              <el-tooltip placement="top">
                <template #content>
                  <div>买入时间 {{ toHHmm(row.ts) }}</div>
                  <div>买入价格 {{ formatPrice(row.buy_price) }}</div>
                  <div>买入量 {{ formatWanHand(row.volume_wan_hand) }}</div>
                  <div>换手 {{ formatTurnover(row.turnover_ratio) }}</div>
                  <div>买入金额 {{ formatYi(Number(row.volume_wan_hand || 0) * 1000000 * Number(row.buy_price || 0)) }}</div>
                </template>
                <div class="pair">
                  <span class="value-top">{{ formatPrice(row.buy_price) }}</span>
                  <span class="value-top">{{ formatTurnover(row.turnover_ratio) }}</span>
                </div>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="流通股" min-width="70" align="center">
            <template #default="{row}">
              <span class="value-top float-badge" :class="floatSharesBadgeClass(row.free_float_shares)">{{ formatYi(row.free_float_shares) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="趋势" min-width="70" align="center">
            <template #default="{row}">
              <el-tooltip placement="top">
                <template #content>
                  <div>今日趋势 {{ formatNumber(row.trend_strength) }}</div>
                  <div>昨日趋势 {{ formatNumber(row.trend_d1) }}</div>
                  <div>前日趋势 {{ formatNumber(row.trend_d2) }}</div>
                  <div>最近涨停 {{ formatRecentLimit(row.days_ago, row.limit_stats) }}</div>
                  <div>板块强度 {{ formatNumber(row.sector_momentum) }}</div>
                  <div>主力净买量 <span :class="trendClass(Number(row.bigorder_net_shares_today || 0) / 1000000)">{{ formatNumber(Number(row.bigorder_net_shares_today || 0) / 1000000) }}</span> 万手</div>
                </template>
                <span class="value-top" :class="trendCompareClass(row.trend_strength, row.trend_d1)">{{ formatNumber(row.trend_strength) }}</span>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="5日DDX" min-width="55" align="center">
            <template #default="{row}">
              <el-tooltip placement="top">
                <template #content>
                  <div>
                    5日ddx明细：
                    <span class="ddx-val" :class="ddxClass(row.ddx1)">{{ formatDDX(row.ddx1) }}</span> |
                    <span class="ddx-val" :class="ddxClass(row.ddx2)">{{ formatDDX(row.ddx2) }}</span> |
                    <span class="ddx-val" :class="ddxClass(row.ddx3)">{{ formatDDX(row.ddx3) }}</span> |
                    <span class="ddx-val" :class="ddxClass(row.ddx4)">{{ formatDDX(row.ddx4) }}</span> |
                    <span class="ddx-val" :class="ddxClass(row.ddx5)">{{ formatDDX(row.ddx5) }}</span>
                  </div>
                  <div style="margin-top:4px;">
                    10日ddx明细：
                    <span class="ddx-val" :class="ddxClass(row.ddx6)">{{ formatDDX(row.ddx6) }}</span> |
                    <span class="ddx-val" :class="ddxClass(row.ddx7)">{{ formatDDX(row.ddx7) }}</span> |
                    <span class="ddx-val" :class="ddxClass(row.ddx8)">{{ formatDDX(row.ddx8) }}</span> |
                    <span class="ddx-val" :class="ddxClass(row.ddx9)">{{ formatDDX(row.ddx9) }}</span> |
                    <span class="ddx-val" :class="ddxClass(row.ddx10)">{{ formatDDX(row.ddx10) }}</span>
                  </div>
                </template>
                <span class="ddx-val" :class="ddxClass(ddxSumValue(row))">{{ formatDDXSum(row) }}</span>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="5日涨幅" min-width="55" align="center">
            <template #default="{row}">
              <span class="pct-val" :class="pctClass(recentPct(row, 5))">{{ formatPctWithSign(recentPct(row, 5)) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="10日涨幅" min-width="55" align="center">
            <template #default="{row}">
              <span class="pct-val" :class="pctClass(recentPct(row, 10))">{{ formatPctWithSign(recentPct(row, 10)) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="20日涨幅" min-width="55" align="center">
            <template #default="{row}">
              <span class="pct-val" :class="pctClass(recentPct(row, 20))">{{ formatPctWithSign(recentPct(row, 20)) }}</span>
            </template>
          </el-table-column>
        </el-table>
        </div>
        <div class="batch-actions">
          <el-button type="danger" :disabled="selectionRows.length===0 || batchDeleting" :loading="batchDeleting" @click="batchDeleteSelected">批量删除</el-button>
          <span v-if="selectedIds.length" class="selected-tip">已选 {{ selectedIds.length }} 项</span>
        </div>
        <el-dialog v-model="confirmVisible" title="确认删除" width="460px">
          <div style="padding:8px 0;">将删除以下ID：</div>
          <div style="background:#f5f7fa; border-radius:6px; padding:10px; word-break:break-all; color:#606266;">
            {{ pendingIds.join(',') }}
          </div>
          <template #footer>
            <el-button @click="cancelDelete" :disabled="batchDeleting">取消</el-button>
            <el-button type="danger" @click="confirmDelete" :loading="batchDeleting">确认删除</el-button>
          </template>
        </el-dialog>
        <el-pagination
          class="pager"
          :current-page="pagination.page"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          layout="prev, pager, next"
          @current-change="pageChange"
        />
      </el-col>
      <el-col :span="6">
        <div class="movements-card">
          <div class="movements-header">
            <div class="left">
              <h3>板块异动榜</h3>
              <span class="update-time">{{ lastMovementUpdate || '--:--' }}</span>
            </div>
          </div>
            <div class="movements-body">
            <div v-if="movementsError" class="state error">{{ movementsError }}</div>
            <div v-else-if="movementsLoading && !movements.length" class="state">数据更新中...</div>
            <div v-else-if="!movements.length" class="state">暂无数据</div>
              <ul v-else class="movements-list">
                <li v-for="it in movements" :key="it.board_code || it.board_name" class="movement-item">
                  <div class="movement-title">
                    <span class="board-name">{{ it.board_name }}</span>
                    <span class="board-pct" :class="pctClass(it.pct)">{{ formatPctWithSign(it.pct) }}</span>
                  </div>
                  <div class="movement-time">异动时间：{{ formatEventTime(it.event_ts) }}</div>
                  <div class="movement-meta">
                    <span>主力净流入 <span class="inflow" :class="trendClass(Number(it.main_net || 0))">{{ formatYi(it.main_net) }}</span></span>
                    <span>换手率 {{ formatPercent(it.turnover) }}</span>
                    <!--<span v-if="it.type" class="badge">{{ it.type }}</span>-->
                  </div>
                  <div class="leader-list" v-if="Array.isArray(it.leaders) && it.leaders.length">
                    <span v-for="ld in it.leaders.slice(0,3)" :key="ld.code || ld.name" class="leader-item">
                      {{ ld.name }} {{ formatPctWithSign(ld.pct) }}
                    </span>
                  </div>
                </li>
              </ul>
          </div>
        </div>
      </el-col>
    </el-row>
    <el-dialog v-model="chartVisible" :title="chartTitle" width="880px" top="6vh" @close="disposeChart">
      <div class="modal-left">
          <div class="chart-card">
            <div v-if="chartLoading" class="loading-overlay">
              <span class="loading-dot" v-for="dot in 3" :key="dot" :style="dotStyle(dot - 1)"></span>
            </div>
            <div v-if="chartMode === 'intraday' && (intradayAggNetWanStr || intradayAggTurnoverPctStr)" class="intraday-blue-stamp">
              <span v-if="intradayAggNetWanStr">{{ intradayAggNetWanStr }} 万元</span>
              <span v-if="intradayAggTurnoverPctStr">/换手 {{ intradayAggTurnoverPctStr }}%</span>
            </div>
            <div ref="chartRef" class="chart-box" :style="{ pointerEvents: chipVisible ? 'none' : 'auto' }"></div>
            <div v-if="chartError" class="state error">{{ chartError }}</div>
            <div v-if="chipVisible" class="chip-card">
              <el-button size="small" class="chip-close" @click.stop="closeChip">关闭</el-button>
              <div ref="chipChartRef" class="chip-chart"></div>
            </div>
          </div>
      </div>
    </el-dialog>
</div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import stockClient from '../../utils/stockClient'
import * as echarts from 'echarts'

const list = ref([])
const pagination = ref({ page: 1, pageSize: 30, total: 0 })
const strategy = ref('all')
const sortKey = ref('pct_chg')
const sortOrder = ref('desc')
const filterLeader = ref(false)
const filterRank = ref(false)
const filterPurple = ref(false)
const filterSmile = ref(false)
const filterOpen = ref(false)
const lastUpdate = ref('')
const selectedSectorName = ref('')
const sectors = ref([])
const sectorExpanded = ref(false)
const strategyOptions = [
  { label: '全部', value: 'all' },
  { label: '五红', value: '五红策略' },
  { label: '竞价', value: '集合竞价策略' },
  { label: '开盘', value: '开盘策略' },
  { label: '尾盘', value: '尾盘策略' },
  { label: '预测', value: '明日预测' }
]
const displaySectors = computed(() => (sectorExpanded.value ? sectors.value : sectors.value.slice(0, 8)))
const changed = ref({})
const momentumMap = ref({})
const chartVisible = ref(false)
const chartTitle = ref('分时图')
const chartLoading = ref(false)
const chartError = ref('')
const chartMode = ref('intraday') // 'intraday' | 'kline'
const chartRef = ref(null)
let chart = null
const intradayData = ref([])
const klineData = ref([])
const intradayAggBuys = ref([])
const intradayAggSells = ref([])
const intradayAggNetWanStr = ref('')
const intradayAggTurnoverPctStr = ref('')
const chipVisible = ref(false)
const chipChartRef = ref(null)
let chipChart = null
const selectionRows = ref([])
const onSelectionChange = (rows) => { selectionRows.value = Array.isArray(rows) ? rows : [] }
const selectedIds = computed(() => {
  return selectionRows.value
    .map(r => {
      const v = r?.id ?? r?.stock_advice_id ?? r?.advice_id
      const n = Number(v)
      return Number.isFinite(n) ? n : null
    })
    .filter(v => v !== null)
})
const batchDeleting = ref(false)
const confirmVisible = ref(false)
const pendingIds = ref([])
const openDeleteConfirm = () => {
  const ids = selectedIds.value
  const codes = selectionRows.value
    .map(r => String(r?.code6 || r?.ts_code || '').replace(/[^0-9]/g, '').slice(0, 6))
    .filter(c => /^\d{6}$/.test(c))
  if (!ids.length && !codes.length) return
  pendingIds.value = ids.length ? ids : codes
  confirmVisible.value = true
}
const batchDeleteSelected = () => {
  openDeleteConfirm()
}
const cancelDelete = () => { confirmVisible.value = false }
const confirmDelete = async () => {
  const ids = pendingIds.value
  if (!ids.length) return
  // ensure ids are numeric; if not, warn and abort
  if (!ids.every(v => Number.isFinite(Number(v)))) {
    ElMessage.warning('选中记录缺少ID，暂无法批量删除')
    confirmVisible.value = false
    return
  }
  batchDeleting.value = true
  try {
    await stockClient.post('/v1/api/strategy/auction/today', { ids })
    ElMessage.success('批量删除成功')
    confirmVisible.value = false
    selectionRows.value = []
    await reload()
  } catch (e) {
    ElMessage.error(String(e?.message || e))
  } finally {
    batchDeleting.value = false
  }
}
const closeChip = () => {
  chipVisible.value = false
  if (chipChart) {
    try { chipChart.dispose() } catch {}
    chipChart = null
  }
  // 清空容器，避免残留遮挡点击
  if (chipChartRef.value) {
    try { chipChartRef.value.innerHTML = '' } catch {}
  }
}

const movements = ref([])
const movementsLoading = ref(false)
const movementsError = ref('')
const lastMovementUpdate = ref('')
const signalsMap = ref({})
const recentPct = (row, days) => {
  if (days === 5) return Number(row?.pct_5d_recent ?? NaN)
  if (days === 10) return Number(row?.pct_10d_recent ?? NaN)
  if (days === 20) return Number(row?.pct_20d_recent ?? NaN)
  return NaN
}

const pageChange = (p) => {
  pagination.value.page = p
  reload()
}

const reload = async () => {
  await loadList()
}

const setStrategy = (v) => {
  if (strategy.value === v) return
  strategy.value = v
}

const setSector = (name) => {
  selectedSectorName.value = String(name || '')
}

const tableRows = computed(() => {
  return list.value
})

const openIntraday = async (row) => {
  const c6 = String(row?.code6 || row?.ts_code || '').replace(/[^0-9]/g, '').slice(0, 6)
  if (!/^\d{6}$/.test(c6)) return
  currentCode6.value = c6
  chartTitle.value = '分时图'
  chartMode.value = 'intraday'
  chartError.value = ''
  chartVisible.value = true
  await nextTick()
  initChart()
  await loadIntradayChart(c6)
  const dayCompact = getIntradayDayCompact()
  await loadIntradayAggFromAgg(c6, dayCompact)
  computeIntradayStamp()
  renderChart()
}
const openKline = async (row) => {
  const c6 = String(row?.code6 || row?.ts_code || '').replace(/[^0-9]/g, '').slice(0, 6)
  if (!/^\d{6}$/.test(c6)) return
  currentCode6.value = c6
  chartTitle.value = 'K线'
  chartMode.value = 'kline'
  chartError.value = ''
  chartVisible.value = true
  await nextTick()
  initChart()
  await loadKlineChart(c6)
  renderChart()
}

const initChart = () => {
  if (chartRef.value) {
    chart = echarts.init(chartRef.value)
  }
}
const disposeChart = () => {
  if (chart) {
    chart.dispose()
    chart = null
  }
  if (chipChart) {
    chipChart.dispose()
    chipChart = null
  }
}
const renderChart = () => {
  if (!chart) return
  chart.clear()
  if (chartMode.value === 'intraday') {
    const times = generateTradingMinutes()
    const closeMap = new Map(intradayData.value.map(item => [toHHmm(item.time), item.close]))
    const closes = times.map(t => (closeMap.has(t) ? closeMap.get(t) : null))
    const buyBars = intradayAggMinute(times, intradayAggBuys.value, chooseBuyColor)
    const sellBars = intradayAggMinute(times, intradayAggSells.value, chooseSellColor)
    const buySegments = buildAggSegments(times, intradayAggBuys.value, 'buy')
    const sellSegments = buildAggSegments(times, intradayAggSells.value, 'sell')
    const pre = basicsPreClose.value
    const baseLimit = computeLimitByCode(currentCode6.value, basicsName.value)
    const todayPct = basicsTodayPct.value
    const lastC = lastFinite(closes)
    const scalePct = Number.isFinite(todayPct) ? computeScaleByPct(todayPct, baseLimit) : computeScalePct(lastC, pre, baseLimit)
    const [yMin, yMax] = computeYAxisRange(pre, scalePct)
    chart.setOption({
      animation: true,
      grid: [
        { left: 40, right: 20, top: 30, height: '65%' },
        { left: 40, right: 20, bottom: 30, height: '20%' }
      ],
      xAxis: [
        { type: 'category', data: times, axisLabel: { color: '#6b7280', formatter: (v) => (shouldShowTimeLabel(v) ? v : ''), showMinLabel: true, showMaxLabel: true }, axisLine: { lineStyle: { color: '#94a3b8' } }, axisTick: { show: true } },
        { type: 'category', data: times, gridIndex: 1, axisLabel: { color: '#94a3b8', formatter: (v) => (shouldShowTimeLabel(v) ? v : ''), showMinLabel: true, showMaxLabel: true }, axisLine: { lineStyle: { color: '#94a3b8' } }, axisTick: { show: true } }
      ],
      yAxis: [
        { type: 'value', min: yMin, max: yMax, axisLabel: { color: '#6b7280' }, splitLine: { lineStyle: { color: '#e7eef6' } } },
        { type: 'value', gridIndex: 1, axisLabel: { color: '#94a3b8' }, splitLine: { lineStyle: { color: '#eef2f7' } } }
      ],
      series: [
        { type: 'line', data: closes, smooth: true, showSymbol: false, lineStyle: { color: '#175a91', width: 2 }, xAxisIndex: 0, yAxisIndex: 0 },
        { type: 'lines', name: '参考-买', coordinateSystem: 'cartesian2d', data: buySegments, xAxisIndex: 0, yAxisIndex: 0, z: 5, lineStyle: { width: 1 } },
        { type: 'lines', name: '参考-卖', coordinateSystem: 'cartesian2d', data: sellSegments, xAxisIndex: 0, yAxisIndex: 0, z: 5, lineStyle: { width: 1 } },
        { type: 'bar', name: '大单买量', data: buyBars, xAxisIndex: 1, yAxisIndex: 1, barWidth: 2, barGap: '0%', itemStyle: { color: '#f04438' } },
        { type: 'bar', name: '大单卖量', data: sellBars, xAxisIndex: 1, yAxisIndex: 1, barWidth: 2, barGap: '0%', itemStyle: { color: '#16a34a' } }
      ]
    })
  } else {
    const dates = klineData.value.map(item => item.time)
    const kdata = klineData.value.map(item => [item.open, item.close, item.low, item.high])
    const volumes = klineData.value.map(item => ({
      value: item.volume,
      itemStyle: { color: item.close >= item.open ? '#f04438' : '#16a34a' }
    }))
    const bigPos = klineData.value.map(item => {
      const raw = Number(item.bigorder_net_shares ?? 0)
      const v = Math.abs(raw) / 1_000_000
      return raw > 0 ? v : null
    })
    const bigNeg = klineData.value.map(item => {
      const raw = Number(item.bigorder_net_shares ?? 0)
      const v = Math.abs(raw) / 1_000_000
      return raw < 0 ? v : null
    })
    chart.setOption({
      animation: true,
      tooltip: { trigger: 'none' },
      grid: [
        { left: 40, right: 20, top: 20, height: '50%' },
        { left: 40, right: 20, top: '62%', height: '12%' },
        { left: 40, right: 20, top: '76%', bottom: 28 }
      ],
      xAxis: [
        { type: 'category', data: dates, boundaryGap: false, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { show: false } },
        { type: 'category', data: dates, gridIndex: 1, boundaryGap: false, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { show: false } },
        { type: 'category', data: dates, gridIndex: 2, boundaryGap: false, axisLine: { lineStyle: { color: '#94a3b8' } }, axisLabel: { color: '#94a3b8', fontSize: 8 } }
      ],
      yAxis: [
        { scale: true, axisLine: { lineStyle: { color: '#94a3b8' } } },
        { scale: true, gridIndex: 1, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { show: false }, splitLine: { show: false }, name: '成交量', nameLocation: 'end', nameGap: 4, nameRotate: 0, nameTextStyle: { color: '#94a3b8', fontSize: 10, align: 'left', verticalAlign: 'top' } },
        { scale: true, gridIndex: 2, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { show: false }, splitLine: { show: false }, name: '大单净量（万手）', nameLocation: 'end', nameGap: 4, nameRotate: 0, nameTextStyle: { color: '#94a3b8', fontSize: 10, align: 'left', verticalAlign: 'top' } }
      ],
      series: [
        { type: 'candlestick', data: kdata, name: 'K线', xAxisIndex: 0, yAxisIndex: 0, itemStyle: { color: '#f04438', color0: '#16a34a', borderColor: '#f04438', borderColor0: '#16a34a' } },
        { type: 'bar', data: volumes, name: '成交量', xAxisIndex: 1, yAxisIndex: 1, barWidth: '60%' },
        { type: 'bar', name: '大单净量-买', xAxisIndex: 2, yAxisIndex: 2, data: bigPos, barWidth: '60%', itemStyle: { color: '#f04438' }, barGap: '-100%' },
        { type: 'bar', name: '大单净量-卖', xAxisIndex: 2, yAxisIndex: 2, data: bigNeg, barWidth: '60%', itemStyle: { color: '#16a34a' }, barGap: '-100%' }
      ]
    })
    chart.off('click')
    chart.on('click', async (params) => {
      if (chipVisible.value) return
      if (!params || params.componentType !== 'series') return
      if (params.seriesName !== '大单净量-买' && params.seriesName !== '大单净量-卖') return
      const idx = Number(params.dataIndex)
      if (!Number.isInteger(idx)) return
      const dayStr = String(klineData.value[idx]?.time || '')
      if (!dayStr) return
      chipVisible.value = true
      await nextTick()
      await loadChipForDay(dayStr)
    })
  }
}
const loadIntradayChart = async (code6) => {
  chartLoading.value = true
  try {
    const resp = await stockClient.get('/v1/api/intraday/kline', { params: { code: code6, freq: '1m' } })
    const list = Array.isArray(resp?.data?.data) ? resp.data.data : []
    intradayData.value = list.map(item => ({
      time: item.ts || item.time || '',
      open: Number(item.o ?? item.open ?? 0),
      high: Number(item.h ?? item.high ?? 0),
      low: Number(item.l ?? item.low ?? 0),
      close: Number(item.c ?? item.close ?? 0),
      volume: Number(item.v ?? item.volume ?? 0)
    }))
    intradayData.value.sort((a, b) => parseMs(a.time) - parseMs(b.time))
    chartError.value = ''
  } catch (e) {
    chartError.value = String(e?.message || e || '加载分时失败')
    intradayData.value = []
  } finally {
    chartLoading.value = false
  }
}
const loadIntradayAgg = async (code6) => {
  try {
    const resp = await stockClient.get('/v1/api/monitor/agg_multi', { params: { stock_code: code6, details: 500 } })
    const items = Array.isArray(resp?.data?.data?.items) ? resp.data.data.items : []
    const row = items.find(x => String(x?.stock_code || '').slice(0,6) === code6) || items[0] || {}
    const buys = Array.isArray(row?.buy_details) ? row.buy_details.map(d => ({ ts: String(d.ts||''), price: Number(d.price||0), vol: Number(d.vol||0) })) : []
    const sells = Array.isArray(row?.sell_details) ? row.sell_details.map(d => ({ ts: String(d.ts||''), price: Number(d.price||0), vol: Number(d.vol||0) })) : []
    intradayAggBuys.value = buys
    intradayAggSells.value = sells
  } catch {
    intradayAggBuys.value = []
    intradayAggSells.value = []
  }
}
const getIntradayDayCompact = () => {
  const first = intradayData.value[0]
  if (first && first.time) {
    const ms = parseMs(first.time)
    if (Number.isFinite(ms)) {
      const d = new Date(ms)
      const y = d.getFullYear()
      const m = String(d.getMonth()+1).padStart(2,'0')
      const day = String(d.getDate()).padStart(2,'0')
      return `${y}${m}${day}`
    }
  }
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth()+1).padStart(2,'0')
  const day = String(d.getDate()).padStart(2,'0')
  return `${y}${m}${day}`
}
const loadIntradayAggFromAgg = async (code6, dayCompact) => {
  try {
    const market = /^6|^688/.test(code6) ? 'SH' : 'SZ'
    const full = `${code6}.${market}`
    const res = await stockClient.get('/v1/api/monitor/agg', { params: { codes: full, details: 500, day: dayCompact } })
    const items = Array.isArray(res?.data?.data?.items) ? res.data.data.items : []
    const first = items[0] || {}
    const buys = Array.isArray(first?.buy_details) ? first.buy_details.map(d => ({ ts: String(d.ts||''), price: Number(d.price||0), vol: Number(d.vol||0) })) : []
    const sells = Array.isArray(first?.sell_details) ? first.sell_details.map(d => ({ ts: String(d.ts||''), price: Number(d.price||0), vol: Number(d.vol||0) })) : []
    intradayAggBuys.value = buys
    intradayAggSells.value = sells
  } catch {
    intradayAggBuys.value = []
    intradayAggSells.value = []
  }
}
const computeIntradayStamp = () => {
  const buySum = intradayAggBuys.value.reduce((s, d) => s + Number(d.price||0) * Number(d.vol||0), 0)
  const sellSum = intradayAggSells.value.reduce((s, d) => s + Number(d.price||0) * Number(d.vol||0), 0)
  const netWan = (buySum - sellSum) / 10000
  intradayAggNetWanStr.value = Number.isFinite(netWan) ? netWan.toFixed(2) : ''
  const totalVol = intradayAggBuys.value.reduce((s, d) => s + Number(d.vol||0), 0) + intradayAggSells.value.reduce((s, d) => s + Number(d.vol||0), 0)
  const free = Number(basicsFreeFloat.value || 0)
  const pct = Number.isFinite(totalVol) && Number.isFinite(free) && free > 0 ? (totalVol / free) * 100 : NaN
  intradayAggTurnoverPctStr.value = Number.isFinite(pct) ? pct.toFixed(2) : ''
}
const basicsFreeFloat = computed(() => {
  const row = list.value.find(r => r.code6 && String(r.code6).slice(0,6) === currentCode6.value) || null
  return Number(row?.free_float_shares || NaN)
})
const basicsPreClose = computed(() => {
  const row = list.value.find(r => r.code6 && String(r.code6).slice(0,6) === currentCode6.value) || null
  return Number(row?.pre_close ?? NaN)
})
const basicsName = computed(() => {
  const row = list.value.find(r => r.code6 && String(r.code6).slice(0,6) === currentCode6.value) || null
  return String(row?.name || '')
})
const basicsTodayPct = computed(() => {
  const row = list.value.find(r => r.code6 && String(r.code6).slice(0,6) === currentCode6.value) || null
  return Number(row?.pct_chg ?? NaN)
})
const currentCode6 = ref('')
const BUY_MIN = 0.0004
const SELL_MIN = 0.0004
const chooseBuyColor = (ratio) => {
  const r = Number(ratio)
  if (!Number.isFinite(r)) return '#a1b4c8'
  if (r > 0.001) return '#6b21a8'
  if (r > 0.0008) return '#b40000'
  if (r > BUY_MIN) return '#ff0000'
  return '#a1b4c8'
}
const chooseSellColor = (ratio) => {
  const r = Number(ratio)
  if (!Number.isFinite(r)) return '#a1b4c8'
  if (r > 0.001) return '#008000'
  if (r > 0.0008) return '#00ff00'
  if (r > SELL_MIN) return '#aaffaa'
  return '#a1b4c8'
}
const intradayAggMinute = (times, details, chooseColor) => {
  const map = new Map()
  for (const d of details) {
    const t = toHHmm(d.ts)
    map.set(t, (map.get(t)||0) + Number(d.vol||0))
  }
  const free = Number(basicsFreeFloat.value || 0)
  return times.map(t => {
    const key = String(t||'')
    const vol = Number(map.get(key)||0)
    const v = vol / 1_000_000
    const ratio = free > 0 ? vol / free : NaN
    const color = Number.isFinite(ratio) ? chooseColor(ratio) : '#a1b4c8'
    return Number.isFinite(v) && v > 0 ? { value: v, itemStyle: { color } } : null
  })
}
const buildAggSegments = (times, details, kind) => {
  if (!Array.isArray(details) || !details.length) return []
  const maxVol = Math.max(...details.map(d => Number(d.vol||0)))
  const x1 = Math.max(0, times.length - 2)
  const maxLen = Math.floor(times.length * 0.20)
  return details.map(d => {
    const vol = Number(d.vol||0)
    const len = Math.max(2, Math.round((vol / (maxVol || 1)) * maxLen))
    const x0 = Math.max(0, x1 - len)
    const y = Number(d.price||0)
    const color = kind === 'buy' ? '#6b21a8' : '#16a34a'
    return { coords: [[x0, y], [x1, y]], lineStyle: { color, opacity: 0.5 } }
  })
}
const computeLimitByCode = (code6, name) => {
  const nm = String(name || '').toUpperCase()
  if (nm.startsWith('ST') || nm.startsWith('*ST')) return 5
  if (/^300/.test(code6) || /^301/.test(code6) || /^688/.test(code6)) return 20
  return 10
}
const computeScalePct = (lastClose, preClose, baseLimit) => {
  const lc = Number(lastClose), pc = Number(preClose)
  if (!Number.isFinite(lc) || !Number.isFinite(pc) || pc <= 0) return baseLimit
  const pct = Math.abs((lc - pc) / pc) * 100
  if (baseLimit === 5) return 5
  if (pct < 3) return 5
  if (baseLimit === 20 && pct >= 10) return 20
  return 10
}
const computeScaleByPct = (todayPct, baseLimit) => {
  const p = Number(todayPct)
  if (!Number.isFinite(p)) return baseLimit
  if (baseLimit === 5) return 5
  if (p < 3 && p > -3) return 5
  if (baseLimit === 20 && Math.abs(p) >= 10) return 20
  return 10
}
const lastFinite = (arr) => {
  for (let i = arr.length - 1; i >= 0; i--) {
    const v = Number(arr[i])
    if (Number.isFinite(v)) return v
  }
  return NaN
}
const computeYAxisRange = (preClose, scalePct) => {
  const pc = Number(preClose)
  const s = Number(scalePct)
  if (!Number.isFinite(pc) || !Number.isFinite(s) || pc <= 0) return [null, null]
  const min = pc * (1 - s / 100)
  const max = pc * (1 + s / 100)
  return [Number(min.toFixed(4)), Number(max.toFixed(4))]
}
const loadKlineChart = async (code6) => {
  chartLoading.value = true
  try {
    const resp = await stockClient.get('/v1/api/kline/with_realtime', { params: { code: code6, freq: 'D', page: 1, page_size: 60 } })
    const list = Array.isArray(resp?.data?.data) ? resp.data.data : []
    const mapped = list.map(item => ({
      time: item.trade_date || item.time || item.ts || '',
      open: Number(item.open ?? item.close ?? 0),
      close: Number(item.close ?? item.open ?? 0),
      low: Number(item.low ?? item.close ?? 0),
      high: Number(item.high ?? item.close ?? 0),
      volume: Number(item.volume ?? 0),
      bigorder_net_shares: Number(item.bigorder_net_shares ?? 0)
    }))
    mapped.sort((a, b) => parseMs(a.time) - parseMs(b.time))
    klineData.value = mapped
    chartError.value = ''
  } catch (e) {
    chartError.value = String(e?.message || e || '加载K线失败')
    klineData.value = []
  } finally {
    chartLoading.value = false
  }
}
const loadChipForDay = async (dayStr) => {
  try {
    if (!chipChartRef.value) return
    if (!chipChart) chipChart = echarts.init(chipChartRef.value)
    const dateOnly = String(dayStr || '').split(' ')[0]
    const compact = dateOnly.includes('-') ? dateOnly.replace(/-/g, '') : dateOnly.slice(0, 8)
    const code6 = String(currentCode6.value || '').slice(0, 6)
    const market = code6 && (/^6|^688/.test(code6)) ? 'SH' : 'SZ'
    const full = `${code6}.${market}`
    let res = await stockClient.get('/v1/api/monitor/agg', { params: { codes: full, details: 500, day: compact } })
    let items = Array.isArray(res?.data?.data?.items) ? res.data.data.items : []
    if (!items.length) {
      res = await stockClient.get('/v1/api/monitor/agg', { params: { codes: code6, details: 500, day: compact } })
      items = Array.isArray(res?.data?.data?.items) ? res.data.data.items : []
    }
    const first = items[0] || {}
    const buys = Array.isArray(first?.buy_details) ? first.buy_details : []
    const sells = Array.isArray(first?.sell_details) ? first.sell_details : []
    const priceSet = new Set()
    buys.forEach(d => { if (Number(d?.vol)>0) priceSet.add(Number(d?.price||0)) })
    sells.forEach(d => { if (Number(d?.vol)>0) priceSet.add(Number(d?.price||0)) })
    const prices = Array.from(priceSet).sort((a,b)=>a-b)
    const sumMap = (arr) => {
      const m = new Map()
      for (const d of arr) {
        const p = Number(d?.price||0); const v = Number(d?.vol||0)
        if (!v) continue
        m.set(p, (m.get(p)||0) + v)
      }
      return m
    }
    const buyMap = sumMap(buys)
    const sellMap = sumMap(sells)
    const free = Number(basicsFreeFloat.value || 0)
    const buyVals = prices.map(p => {
      const vol = Number(buyMap.get(p) || 0)
      const v = Number(((vol) / 1_000_000).toFixed(4))
      const ratio = free > 0 ? vol / free : NaN
      return { value: v, itemStyle: { color: chooseBuyColor(ratio) } }
    })
    const sellVals = prices.map(p => {
      const vol = Number(sellMap.get(p) || 0)
      const v = Number(((vol) / 1_000_000).toFixed(4))
      const ratio = free > 0 ? vol / free : NaN
      return { value: v, itemStyle: { color: chooseSellColor(ratio) } }
    })
    const maxAbs = Math.max(...[...buyVals, ...sellVals].map(d => Math.abs(Number(d?.value||0))), 0)
    const sellNeg = sellVals.map(d => ({ value: -Math.abs(Number(d?.value||0)), itemStyle: d.itemStyle }))
    chipChart.setOption({
      animation: true,
      grid: { left: 60, right: 40, top: 30, bottom: 30 },
      xAxis: { type: 'value', min: -maxAbs * 1.15, max: maxAbs * 1.15, axisLine: { lineStyle: { color: '#94a3b8' } }, axisLabel: { color: '#94a3b8' }, splitLine: { lineStyle: { color: '#eef2f7' } } },
      yAxis: { type: 'category', data: prices.map(p => Number(p).toFixed(2)), axisLine: { lineStyle: { color: '#94a3b8' } }, axisLabel: { color: '#94a3b8' } },
      series: [
        { type: 'bar', name: '卖', data: sellNeg, barWidth: 1, z: 3, itemStyle: { color: '#16a34a' } },
        { type: 'bar', name: '买', data: buyVals, barWidth: 1, z: 4, itemStyle: { color: '#f04438' }, barGap: '0%' }
      ]
    })
  } catch (e) {}
}
const loadAvg5 = async (code6) => {
  avg5Loading.value = true
  avg5Error.value = ''
  try {
    const days = last5TradingDays()
    const rows = []
    for (const day of days) {
      const market = /^6|^688/.test(code6) ? 'SH' : 'SZ'
      const full = `${code6}.${market}`
      const res = await stockClient.get('/v1/api/monitor/agg', { params: { codes: full, details: 200, day: day.replace(/-/g,'') } })
      const items = Array.isArray(res?.data?.data?.items) ? res.data.data.items : []
      const first = items[0] || {}
      const buys = Array.isArray(first?.buy_details) ? first.buy_details : []
      const sells = Array.isArray(first?.sell_details) ? first.sell_details : []
      const buySum = buys.reduce((s,d)=>s + Number(d?.price||0)*Number(d?.vol||0), 0)
      const buyVol = buys.reduce((s,d)=>s + Number(d?.vol||0), 0)
      const sellSum = sells.reduce((s,d)=>s + Number(d?.price||0)*Number(d?.vol||0), 0)
      const sellVol = sells.reduce((s,d)=>s + Number(d?.vol||0), 0)
      rows.push({ day, avgBuy: buyVol>0 ? buySum/buyVol : NaN, avgSell: sellVol>0 ? sellSum/sellVol : NaN })
    }
    avg5Rows.value = rows
  } catch (e) {
    avg5Error.value = String(e?.message || e || '加载参考失败')
    avg5Rows.value = []
  } finally {
    avg5Loading.value = false
  }
}
const last5TradingDays = () => {
  const out = []
  const base = new Date()
  let i = 0
  while (out.length < 5 && i < 14) {
    const d = new Date(base.getTime() - i*24*3600*1000)
    const wk = d.getDay()
    if (wk !== 0 && wk !== 6) {
      const y = d.getFullYear()
      const m = String(d.getMonth()+1).padStart(2,'0')
      const day = String(d.getDate()).padStart(2,'0')
      out.push(`${y}-${m}-${day}`)
    }
    i++
  }
  return out
}
const dotStyle = (index) => ({ animationDelay: `${index * 0.25}s` })
const parseMs = (s) => {
  const v = s
  if (typeof v === 'number') {
    const n = Number(v)
    if (!Number.isFinite(n)) return NaN
    return n > 1e12 ? n : n * 1000
  }
  const t = String(v || '').trim()
  if (!t) return NaN
  if (/^\d{10}$/.test(t)) return Number(t) * 1000
  if (/^\d{13}$/.test(t)) return Number(t)
  const iso = t.includes(' ') ? t.replace(' ', 'T') : t
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : Date.parse(t)
}
const toHHmm = (s) => {
  if (typeof s === 'number') {
    const ms = s > 1e12 ? s : s * 1000
    const d = new Date(ms)
    if (Number.isNaN(d.getTime())) return ''
    const hh = String(d.getHours()).padStart(2,'0')
    const mm = String(d.getMinutes()).padStart(2,'0')
    return `${hh}:${mm}`
  }
  const t = String(s || '').trim()
  if (!t) return ''
  if (/^\d{2}:\d{2}$/.test(t)) return t
  if (/^\d{10}$/.test(t)) return toHHmm(Number(t))
  if (/^\d{13}$/.test(t)) return toHHmm(Number(t))
  const iso = t.includes(' ') ? t.replace(' ', 'T') : t
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const hh = String(d.getHours()).padStart(2,'0')
  const mm = String(d.getMinutes()).padStart(2,'0')
  return `${hh}:${mm}`
}
const generateTradingMinutes = () => {
  const out = []
  const pushRange = (start, end) => {
    let d = new Date()
    const [sh, sm] = start.split(':').map(Number)
    const [eh, em] = end.split(':').map(Number)
    d.setHours(sh, sm, 0, 0)
    const endMs = new Date(d.getFullYear(), d.getMonth(), d.getDate(), eh, em, 0, 0).getTime()
    while (d.getTime() <= endMs) {
      const hh = String(d.getHours()).padStart(2,'0')
      const mm = String(d.getMinutes()).padStart(2,'0')
      out.push(`${hh}:${mm}`)
      d = new Date(d.getTime() + 60_000)
    }
  }
  pushRange('09:25', '11:30')
  pushRange('13:00', '15:00')
  return out
}
const shouldShowTimeLabel = (t) => {
  const s = String(t || '')
  if (!s) return false
  if (s === '09:25' || s === '11:30' || s === '13:00' || s === '15:00') return true
  return s.endsWith(':00') || s.endsWith(':30')
}
const loadList = async () => {
  const params = {
    page: pagination.value.page,
    page_size: pagination.value.pageSize,
    strategy: strategy.value === 'all' ? 'all' : strategy.value,
    sort: sortKey.value,
    order: sortOrder.value,
    ...(selectedSectorName.value ? { sector_name: selectedSectorName.value } : {}),
    ...(filterLeader.value ? { is_leader: 1 } : {}),
    ...(filterRank.value ? { is_rank: 1 } : {}),
    ...(filterPurple.value ? { signal_buy: 0 } : {}),
    ...(filterOpen.value ? {
      today_vol_ratio_gt: 0.6,
      bigorder_net_shares_today: 0,
      price_speed_ratio_gt: 0,
      today_vol_ratio_lt: 18
    } : {})
  }
  try {
    const resp = await stockClient.get('/v1/api/strategy/auction/today', { params })
    const payload = resp?.data?.data || resp?.data || {}
    const items = Array.isArray(payload.items) ? payload.items : []
    sectors.value = (Array.isArray(payload.sectors) ? payload.sectors : [])
      .map(it => ({
        sector_name: String(it?.sector_name || '').trim(),
        count: Number(it?.count || 0)
      }))
      .filter(it => it.sector_name)
    const prevMap = {}
    for (const row of list.value) {
      const k = String(row.code6 || row.ts_code || '').slice(0, 6)
      prevMap[k] = Number(row.pct_chg ?? NaN)
    }
    let nextRows = items.map(it => ({ ...it, code6: String(it.code6 || String(it.ts_code || '').slice(0, 6)) }))
    const nextChanged = {}
    for (const it of nextRows) {
      const k = String(it.code6 || '').slice(0, 6)
      const prev = Number(prevMap[k] ?? NaN)
      const curr = Number(it.pct_chg ?? NaN)
      if (Number.isFinite(prev) && Number.isFinite(curr)) {
        if (curr > prev) nextChanged[k] = 'blink-up'
        else if (curr < prev) nextChanged[k] = 'blink-down'
      }
    }
    changed.value = nextChanged
    lastUpdate.value = new Date().toLocaleTimeString()
    const codes = nextRows.map(it => String(it.code6 || '').slice(0, 6)).filter(c => /^\d{6}$/.test(c))
    if (codes.length) {
      await loadMicroMomentumBatch(codes)
    }
    if (filterSmile.value) {
      nextRows = nextRows.filter(r => displayMomentumSymbol(r) === '😊')
    }
    list.value = nextRows
    pagination.value.total = Number(payload.total || list.value.length || 0)
  } catch (e) {
    ElMessage.error(e.toString())
  }
}

const loadMicroMomentumBatch = async (codes) => {
  try {
    const resp = await stockClient.get('/v1/api/indicators/micro_momentum', { params: { stock_code: codes.join(',') } })
    const list = resp?.data?.list || resp?.data?.data?.list || []
    const next = { ...momentumMap.value }
    for (const item of list) {
      const c6 = String(item?.stock_code || '').replace(/[^0-9]/g, '').slice(0, 6)
      if (!/^\d{6}$/.test(c6)) continue
      next[c6] = {
        micro_vol_ratio: Number(item?.micro_vol_ratio ?? NaN),
        vol_acc: Number(item?.vol_acc ?? NaN),
        time_adjusted_vol_ratio: Number(item?.time_adjusted_vol_ratio ?? NaN),
        price_speed_ratio: Number(item?.price_speed_ratio ?? NaN),
        pct_chg: Number(item?.pct_chg ?? NaN),
        close_latest: Number(item?.close_latest ?? NaN)
      }
    }
    momentumMap.value = next
  } catch (e) {}
}
const loadSignalsBatch = async (codes) => {
  try {
    const resp = await stockClient.get('/v1/api/signals', { params: { code: codes.join(','), basic: 1, page: pagination.value.page, page_size: pagination.value.pageSize } })
    const arr = resp?.data?.data || resp?.data?.list || []
    const next = { ...signalsMap.value }
    for (const it of arr) {
      const c6 = String(it?.code || it?.stock_code || '').replace(/[^0-9]/g, '').slice(0, 6)
      if (!/^\d{6}$/.test(c6)) continue
      next[c6] = {
        pct_5d_recent: Number(it?.pct_5d_recent ?? NaN),
        pct_10d_recent: Number(it?.pct_10d_recent ?? NaN),
        pct_20d_recent: Number(it?.pct_20d_recent ?? NaN)
      }
    }
    signalsMap.value = next
  } catch (e) {}
}

const loadMovements = async () => {
  movementsLoading.value = true
  movementsError.value = ''
  try {
    const resp = await stockClient.get('/v1/api/market/movements', { params: { type: 'industry', limit: 200 } })
    const payload = resp?.data?.data || resp?.data || {}
    const type = payload?.type || ''
    const items = Array.isArray(payload?.items) ? payload.items : []
    movements.value = items.map(it => ({ ...it, type }))
    lastMovementUpdate.value = new Date().toLocaleTimeString()
  } catch (e) {
    movementsError.value = String(e)
    movements.value = []
  } finally {
    movementsLoading.value = false
  }
}

const changedClass = (row) => {
  const k = String(row.code6 || '').slice(0, 6)
  return changed.value[k] || ''
}

const sigBarsVisible = (row) => {
  const b = Number(row.signal_buy ?? 0)
  const s = Number(row.signal_sell ?? 0)
  return Number.isFinite(b) || Number.isFinite(s)
}
const barStyleBuy = (v) => {
  const n = Number(v ?? NaN)
  if (!Number.isFinite(n) || n <= 0) return { display: 'none' }
  return { height: `${Math.min(10, Math.max(1, Math.round(n)))}px` }
}
const barStyleSell = (v) => {
  const n = Number(v ?? NaN)
  if (!Number.isFinite(n) || n <= 0) return { display: 'none' }
  return { height: `${Math.min(10, Math.max(1, Math.round(n)))}px` }
}

const displayMomentumSymbol = (row) => {
  const c6 = String(row.code6 || '').replace(/[^0-9]/g, '').slice(0, 6)
  const ind = momentumMap.value[c6]
  if (!ind) return ''
  const psr = Number(ind.price_speed_ratio ?? NaN)
  const mvr = Number(ind.micro_vol_ratio ?? NaN)
  const va = Number(ind.vol_acc ?? NaN)
  const pct = Number(ind.pct_chg ?? NaN)
  const EPS = 0.05
  const inc = Number.isFinite(va) && va > EPS
  const dec = Number.isFinite(va) && va < -EPS
  if (Number.isFinite(psr) && psr > 1.5) {
    if (inc) return '😊'
    return '😭'
  } else if (Number.isFinite(psr) && psr < -1.0) {
    if (mvr < 1.0 && va < EPS) return '😊'
    if (mvr > 1.5 && inc) return '😭'
    return '😊'
  } else {
    if (pct > 0 && mvr > 1.8 && dec) return '😭'
    if (pct < 0 && mvr < 1.0 && va < EPS) return '😊'
    return '😊'
  }
}

const displayName = (name) => {
  const n = String(name || '').trim()
  return n || '--'
}
const displayCode = (tsCode) => {
  const c6 = String(tsCode || '').replace(/[^0-9]/g, '').slice(0, 6)
  return c6 || '--'
}

const formatPrice = (v) => {
  if (!Number.isFinite(Number(v))) return '--'
  return Number(v).toFixed(2)
}
const formatTurnover = (v) => {
  if (!Number.isFinite(Number(v))) return '--'
  return `${Number(v).toFixed(2)}%`
}
const formatWanHand = (v) => {
  if (!Number.isFinite(Number(v))) return '--'
  return Number(v).toFixed(2)
}
const formatYi = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return '--'
  return (n / 100000000).toFixed(2) + '亿'
}
const formatNumber = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return '--'
  return n.toFixed(2)
}
const formatInt = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return '--'
  return String(Math.round(n))
}
const formatRecentLimit = (days, times) => {
  const d = Number(days)
  const t = Number(times)
  const ds = Number.isFinite(d) ? String(Math.round(d)) : '--'
  const ts = Number.isFinite(t) ? String(Math.round(t)) : '--'
  if (ds === '--' && ts === '--') return '--'
  return `${ds}天前/${ts}次`
}
const formatDDX = (v) => {
  if (!Number.isFinite(Number(v))) return '--'
  return (Number(v) / 1000000).toFixed(2)
}
const ddxClass = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return 'neutral'
  if (n > 0) return 'positive'
  if (n < 0) return 'negative'
  return 'neutral'
}
const pctClass = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n) || n === 0) return 'neutral'
  return n > 0 ? 'positive' : 'negative'
}
const trendClass = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n) || n === 0) return 'neutral'
  return n > 0 ? 'positive' : 'negative'
}
const trendCompareClass = (curr, prev) => {
  const a = Number(curr)
  const b = Number(prev)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 'neutral'
  if (a > b) return 'positive'
  if (a < b) return 'negative'
  return 'neutral'
}
const formatPctWithSign = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return ''
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toFixed(2)}%`
}
const formatPctIntWithSign = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return ''
  const sign = n > 0 ? '+' : ''
  return `${sign}${Math.round(n)}%`
}
const formatPercent = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return '--'
  return `${n.toFixed(2)}%`
}
const formatSpeed30 = (ratio) => {
  const r = Number(ratio)
  if (!Number.isFinite(r)) return '--'
  const speed30 = r * 30 * 100
  return `${speed30.toFixed(2)}%/30分钟`
}
const priceSpeedBadgeClass = (ratio) => {
  const r = Number(ratio)
  if (!Number.isFinite(r)) return 'float-badge--gray'
  const speed30 = r * 30 * 100
  if (speed30 <= 0.3) return 'float-badge--gray'
  if (speed30 <= 0.8) return 'float-badge--yellow'
  return 'float-badge--red'
}
const floatSharesBadgeClass = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return 'float-badge--gray'
  const yi = n / 100000000
  if (yi < 20) return 'float-badge--red'
  if (yi < 60) return 'float-badge--yellow'
  return 'float-badge--green'
}
const formatEventTime = (ts) => {
  const n = Number(ts)
  if (!Number.isFinite(n) || n <= 0) return '--:--:--'
  try {
    const d = new Date(n * 1000)
    return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`
  } catch { return '--:--:--' }
}
const formatDDXSum = (row) => {
  const vals = [row?.ddx1, row?.ddx2, row?.ddx3, row?.ddx4, row?.ddx5].map(v => Number(v)).filter(v => Number.isFinite(v))
  if (!vals.length) return '--'
  const sum = vals.reduce((s, v) => s + v, 0)
  return (sum / 1000000).toFixed(2)
}
const ddxSumValue = (row) => {
  const vals = [row?.ddx1, row?.ddx2, row?.ddx3, row?.ddx4, row?.ddx5].map(v => Number(v)).filter(v => Number.isFinite(v))
  if (!vals.length) return 0
  return vals.reduce((s, v) => s + v, 0)
}
const ddxDetails = (row) => {
  const arr = [row?.ddx1, row?.ddx2, row?.ddx3, row?.ddx4, row?.ddx5]
  const parts = arr.map(v => formatDDX(v)).filter(s => s !== '--')
  return parts.length ? parts.join(' | ') : '--'
}
const scoreEmoji = (v) => {
  const num = Number(v)
  if (!Number.isFinite(num)) return ''
  const arr = ['0️⃣','1️⃣','2️⃣','3️⃣','4️⃣','5️⃣','6️⃣','7️⃣','8️⃣','9️⃣','🔟']
  if (num >= 0 && num <= 10) return arr[num]
  return String(num)
}

onMounted(async () => {
  await Promise.all([loadList(), loadMovements()])
})

watch([filterLeader, filterRank, filterPurple, filterSmile, filterOpen], async () => {
  pagination.value.page = 1
  await loadList()
})

watch([strategy, selectedSectorName], async () => {
  pagination.value.page = 1
  await loadList()
})
</script>

<style scoped>
.auction-monitor { display: flex; flex-direction: column; gap: 12px; }
.toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.update-time { color: #909399; font-size: 12px; }
.filters { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
 .name-cell { display: flex; flex-direction: column; gap: 4px; align-items: flex-start; }
 .name-cell .row1 { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
 .name-cell .row1 .left { display: flex; flex-direction: column; gap: 2px; align-items: flex-start; }
 .name-cell .row1 .right { display: inline-flex; align-items: center; gap: 6px; }
.name-cell .nm { font-weight: 600; color: #175a91; }
.name-cell .cd { font-size: 10px; color: #6b7280; }
.name-cell .flags { display: inline-flex; align-items: center; gap: 6px; }
.name-cell .sig-bars { display: inline-flex; align-items: flex-end; gap: 2px; height: 10px; }
.name-cell .sig-bars .bar { width: 6px; border-radius: 2px; display: inline-block; }
.name-cell .sig-bars .bar.buy { background: #6b21a8; }
.name-cell .sig-bars .bar.sell { background: #098551; }
.name-cell .leader { font-size: 14px; }
.name-cell .strength { font-size: 14px; white-space: nowrap; }
.name-cell .score { font-size: 12px; background: rgba(23, 90, 145, 0.10); color: #175a91; border-radius: 6px; padding: 0 6px; }
.momentum { font-size: 14px; }
.pair { display: flex; flex-direction: column; }
.value-top { color: #0b1f33; }
.value-top.positive { color: #d81e06; }
.value-top.negative { color: #098551; }
.value-top.neutral { color: #6b7280; }
.price-cell { display: flex; flex-direction: column; align-items: center; }
.curr { font-weight: 600; position: relative; }
.curr .curr-sup { font-size: 10px; vertical-align: super; margin-left: 4px; color: inherit; }
.curr.positive { color: #d81e06; }
.curr.negative { color: #098551; }
.curr.neutral { color: #6b7280; }
.ddx { display: inline-flex; align-items: center; gap: 2px; font-size: 12px; }
.ddx-val.positive { color: #d81e06; }
.ddx-val.negative { color: #098551; }
.ddx-val.neutral { color: #6b7280; }
.divider { color: #98a2b3; }
.blink-up { animation: flashUp 0.8s ease; }
.blink-down { animation: flashDown 0.8s ease; }
@keyframes flashUp { 0% { background: rgba(216,30,6,0.15);} 100% { background: transparent; } }
@keyframes flashDown { 0% { background: rgba(9,133,81,0.15);} 100% { background: transparent; } }
.pager { margin-top: 10px; text-align: right; }
.movements-card { background: #fff; border-radius: 8px; box-shadow: 0 8px 16px rgba(23,90,145,0.08); padding: 10px; min-height: 400px; }
.movements-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.movements-header .left { display: flex; align-items: center; gap: 8px; }
.movements-header h3 { margin: 0; color: #175a91; }
.movements-body .state { padding: 12px; text-align: center; color: #909399; }
.movements-body .state.error { color: #d81e06; }
.movements-list { display: flex; flex-direction: column; gap: 12px; max-height: 70vh; overflow-y: auto; list-style: none; margin: 0; padding: 0; }
.movement-item { border-bottom: 1px solid rgba(23,90,145,0.10); padding-bottom: 10px; }
.movement-title { display: flex; align-items: center; justify-content: space-between; }
.movement-title .board-name { font-weight: 600; color: #175a91; font-size: 15px; }
.movement-title .board-pct { font-weight: 700; font-size: 15px; }
.movement-title .board-pct.positive { color: #d81e06; }
.movement-title .board-pct.negative { color: #1a73e8; }
.movement-title .board-pct.neutral { color: #6b7280; }
.movement-time { margin-top: 4px; font-size: 12px; color: #6b7280; }
.movement-meta { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 4px; font-size: 12px; align-items: baseline; }
.movement-meta .badge { font-size: 12px; color: #175a91; background: rgba(23,90,145,0.1); border-radius: 6px; padding: 0 6px; }
.inflow.positive { color: #d81e06; }
.inflow.negative { color: #1a73e8; }
.leader-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.leader-item { background: rgba(23, 90, 145, 0.08); color: #175a91; border-radius: 6px; padding: 4px 8px; font-size: 12px; }
.auction-monitor :deep(.el-table .cell) { padding: 2px 6px; line-height: 1.1; }
.table-scroll { overflow-x: auto; }
.modal-left { display: grid; gap: 10px; }
.chart-card { position: relative; min-height: 420px; }
.chart-box { width: 100%; height: 60vh; min-height: 380px; border-radius: 8px; }
.chip-card { background: #fff; border-radius: 8px; box-shadow: 0 6px 12px rgba(23,90,145,0.06); padding: 8px; z-index: 20; }
.chip-chart { width: 100%; height: 200px; pointer-events: none; }
.float-badge { display: inline-block; padding: 0 6px; border-radius: 6px; border: 1px solid currentColor; }
.float-badge--red { color: #d81e06; }
.float-badge--yellow { color: #facc15; }
.float-badge--green { color: #16a34a; }
.float-badge--gray { color: #6b7280; }
.chip-close { position: absolute; right: 8px; top: 6px; z-index: 30; }
.chart-box { position: relative; }
.loading-overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
.loading-dot { width: 10px; height: 10px; border-radius: 50%; background: #175a91; margin: 0 4px; animation: pulse 1s ease infinite; opacity: .8; }
@keyframes pulse { 0% { transform: scale(0.8); opacity: .5 } 50% { transform: scale(1.2); opacity: 1 } 100% { transform: scale(0.8); opacity: .5 } }
.intraday-blue-stamp { position: absolute; right: 50px; top: -20px; padding: 6px 10px; border-radius: 6px; border: 2px solid #175a91; color: #175a91; background: rgba(23,90,145,0.10); font-weight: 700; font-size: 12px; z-index: 3; }
.chip-card { position: absolute; right: 12px; bottom: 12px; width: 56%; z-index: 4; }
.pct-val.positive { color: #d81e06; }
.pct-val.negative { color: #098551; }
.pct-val.neutral { color: #6b7280; }
</style>
